import { useState } from 'react';
import { type DialogBaseProps } from '@lingoda/dialogs';
import { DialogConfirm, OptionButton, Stack, Typography } from '@lingoda/ui';
import { Buttons } from '../Buttons';

interface Option {
    value: number;
    left: string;
    right: string;
}

interface Props extends Pick<DialogBaseProps, 'hideDialog'> {
    closable?: boolean;
    currentGoal?: number | null;
    loading: boolean;
    onDeleteGoal: () => void;
    onSaveGoal: (selectedValue: number) => void;
    options: Option[];
    title: string;
}

export const Layout = ({
    closable = false,
    currentGoal,
    hideDialog,
    loading,
    onDeleteGoal,
    onSaveGoal,
    options,
    title,
}: Props) => {
    const [selectedValue, setSelectedValue] = useState(currentGoal || UNSELECTED_VALUE);

    const handleSaveGoal = () => {
        onSaveGoal(selectedValue);
    };

    const confirmStatus = loading
        ? 'loading'
        : selectedValue === UNSELECTED_VALUE
          ? 'disabled'
          : 'enabled';

    const disabled = confirmStatus === 'disabled';

    return (
        <DialogConfirm
            isOpen
            title={title}
            onConfirm={handleSaveGoal}
            renderConfirmButton={({ dataCy, onConfirm }) => (
                <Buttons
                    dataCy={dataCy}
                    onConfirm={onConfirm}
                    currentGoal={currentGoal}
                    disabled={disabled}
                    onDeleteGoal={onDeleteGoal}
                />
            )}
            onClose={closable ? hideDialog : undefined}
            confirmStatus={confirmStatus}
            titleProps={{ textAlign: 'center', justifyContent: 'center', display: 'flex' }}
            actionsLayoutVariant="center-auto"
        >
            <Stack spacing={2} mt={2}>
                {options.map(({ value, left, right }) => (
                    <OptionButton
                        key={value}
                        selected={value === selectedValue}
                        sx={{
                            px: 2,
                            py: 2,
                            minHeight: 48,
                            color: 'text.primary',
                        }}
                        onClick={() => setSelectedValue(value)}
                        labelFullWidth
                        disabled={loading}
                    >
                        <Stack
                            direction={{ xs: 'column', sm: 'row' }}
                            justifyContent="space-between"
                            alignItems="center"
                            flex={1}
                        >
                            <Typography variant="body1" fontWeight={600}>
                                {left}
                            </Typography>
                            <Typography variant="body1">{right}</Typography>
                        </Stack>
                    </OptionButton>
                ))}
            </Stack>
        </DialogConfirm>
    );
};

const UNSELECTED_VALUE = -1;
